import React, {useState} from 'react';
import {Box, Typography} from '@mui/material';
import moment from 'moment';
import {GetReviewInvitationDetails_reviewDetails_invitation} from '../../../schema-types/GetReviewInvitationDetails';
import {ContactRAGStatusesFragment} from '../../../schema-types/ContactRAGStatusesFragment';
import {GetInvitationEmailAudit} from '../../../schema-types/GetInvitationEmailAudit';
import {
  ClientPortalStatusCategory,
  OneviewContactAtrStatusChoices,
  OneviewContactEcqStatusChoices,
} from '../../../schema-types/globalTypes';
import {themeColors} from '../../../theme/Theme';
import {enumToLabel} from '../../../utils/typescriptUtils';
import SendAtrQuestionnaireModal from '../../../components/ContactActions/SendAtrQuestionnaireModal';
import {Panel} from '../../../components/Panel/Panel';
import {ColoredChip} from '../../../components/EditorGrid/GridRenderComponents';

interface ContactPhoneProps {
  phoneType: string;
  invitationNumber?: string | null;
  phoneNumber: string;
}

interface InvitationContactDisplayProps {
  contact: ContactRAGStatusesFragment;
  invitation: GetReviewInvitationDetails_reviewDetails_invitation | null;
  accountId: string;
  emailAuditData?: GetInvitationEmailAudit;
  emailAuditLoading: boolean;
  emailAuditError?: any;
}

const ContactPhone: React.FC<ContactPhoneProps> = ({
  phoneType,
  invitationNumber,
  phoneNumber,
}) => {
  return (
    <Box sx={{alignItems: 'center'}}>
      <Typography variant="body2" mt={0.5}>
        <a
          href={`tel:${phoneNumber}`}
          style={{
            alignSelf: 'center',
            color: themeColors.coral,
            fontWeight: invitationNumber === phoneNumber ? 'bold' : 'normal',
          }}
        >
          {phoneNumber}
        </a>
      </Typography>
      {phoneNumber === invitationNumber && (
        <Typography sx={{color: themeColors.green, fontSize: '0.8rem'}}>
          (This {phoneType.toLowerCase()} number above was provided while booking the
          meeting)
        </Typography>
      )}
    </Box>
  );
};

const MeetingPhoneNumbers: React.FC<{
  contact: ContactRAGStatusesFragment;
  invitation: GetReviewInvitationDetails_reviewDetails_invitation | null;
}> = ({contact, invitation}) => {
  const phoneNumbers = [
    {type: 'Primary', number: contact.primaryPhone},
    {type: 'Home', number: contact.homePhone},
    {type: 'Work', number: contact.workPhone},
    {type: 'Secondary', number: contact.secondaryPhone},
  ].filter((phone) => phone.number);

  return (
    <Box>
      <Typography variant="body1" fontWeight="bold">
        Phone Numbers
      </Typography>
      {phoneNumbers.length > 0 ? (
        phoneNumbers.map((phone) => (
          <ContactPhone
            key={`${phone.type}-${phone.number}`}
            phoneType={phone.type}
            phoneNumber={phone.number!}
            invitationNumber={invitation?.phoneNumber || null}
          />
        ))
      ) : (
        <Typography color="text.secondary">No phone numbers available</Typography>
      )}
    </Box>
  );
};

const ContactATRStatus: React.FC<{
  contact: ContactRAGStatusesFragment;
  accountId: string;
}> = ({contact, accountId}) => {
  const [isManageATRModalOpen, setIsManageATRModalOpen] = useState(false);

  const getATRStatus = () => {
    const status = enumToLabel(contact.atrStatus || 'Unknown');

    if (contact.atrStatus === OneviewContactAtrStatusChoices.SENT) {
      return {
        status,
        color: themeColors.lightBlue,
        details: `Sent: ${new Date(
          contact.statuses?.atr?.dateSent
        ).toLocaleDateString()}`,
      };
    }
    if (contact.atrStatus === OneviewContactAtrStatusChoices.IN_DATE) {
      return {
        status,
        color: themeColors.lightYellow,
        details: `In Date until: ${moment(contact.statuses?.atr?.dateSubmitted)
          .add(3, 'years')
          .format('DD/MM/YYYY')}`,
      };
    }
    if (contact.atrStatus === OneviewContactAtrStatusChoices.UP_TO_DATE) {
      return {
        status,
        color: themeColors.lightGreen,
        details: `Up to date until: ${moment(contact.statuses?.atr?.dateSubmitted)
          .add(3, 'years')
          .format('DD/MM/YYYY')}`,
      };
    }

    if (contact.atrStatus === OneviewContactAtrStatusChoices.OUTDATED) {
      return {
        status,
        color: themeColors.lightAlert,
        details: `Outdated since: ${moment(contact.statuses?.atr?.dateSubmitted)
          .add(3, 'years')
          .format('DD/MM/YYYY')}`,
      };
    }
    if (contact.atrStatus === OneviewContactAtrStatusChoices.MISSING) {
      return {
        status,
        color: themeColors.lightAlert,
        details: 'Contact has never completed an RTQ',
      };
    }
    return {
      status,
      color: themeColors.lightAlert,
      details: 'No RTQ data found for this contact',
    };
  };

  const atrStatus = getATRStatus();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body1" fontWeight="bold">
            RTQ Status
          </Typography>
          <ColoredChip
            value={atrStatus.status}
            keepCase
            size="small"
            colorScheme={{[atrStatus.status]: atrStatus.color}}
          />
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {atrStatus.details}
      </Typography>
      <SendAtrQuestionnaireModal
        contact={contact}
        isOpen={isManageATRModalOpen}
        close={() => setIsManageATRModalOpen(false)}
        accountId={accountId!}
      />
    </Box>
  );
};

const ContactEmailStatus: React.FC<{
  contact: ContactRAGStatusesFragment;
}> = ({contact}) => {
  const getEmailStatus = () => {
    const {isEmailUnique, email} = contact;
    if (!email) {
      return {
        status: 'No Email exists',
        color: themeColors.lightAlert,
        details: 'Email address not available',
      };
    }
    if (isEmailUnique === undefined) {
      return {
        status: 'Unable to get email uniqueness',
        color: themeColors.lightAlert,
        details: 'Email uniqueness status not available',
      };
    }

    if (isEmailUnique) {
      return {
        status: 'Email is unique',
        color: themeColors.lightGreen,
        details: 'Email address is unique in the system',
      };
    }

    return {
      status: 'Email is not unique',
      color: themeColors.lightAlert,
      details: 'Email address exists for multiple contacts',
    };
  };

  const emailStatus = getEmailStatus();

  return (
    <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1}}>
      {contact?.email?.length ? (
        <>
          <Typography variant="body1" fontWeight="bold">
            Email{' '}
          </Typography>
          <Typography
            component="a"
            href={`mailto:${contact.email}`}
            sx={{color: themeColors.coral}}
            fontWeight="bold"
          >
            {contact.email}
          </Typography>
          <ColoredChip
            value={emailStatus.status}
            keepCase
            size="small"
            colorScheme={{[emailStatus.status]: emailStatus.color}}
          />
          {/* <Typography variant="body1" fontWeight="bold" color={emailStatus.color}>
            {emailStatus.status}
          </Typography> */}
        </>
      ) : (
        <Typography variant="body1" fontWeight="bold">
          Contact Email
          <ColoredChip
            value={emailStatus.status}
            keepCase
            size="small"
            colorScheme={{[emailStatus.status]: emailStatus.color}}
          />
        </Typography>
      )}
    </Box>
  );
};

const ContactECQStatus: React.FC<{
  contact: ContactRAGStatusesFragment;
}> = ({contact}) => {
  const getECQStatus = () => {
    const status = enumToLabel(contact.ecqStatus || 'Unknown');

    if (contact.ecqStatus === OneviewContactEcqStatusChoices.SENT) {
      return {
        status,
        color: themeColors.lightBlue,
        details: `Sent: ${new Date(
          contact.statuses?.factFind?.dateSent
        ).toLocaleDateString()}`,
      };
    }
    if (contact.ecqStatus === OneviewContactEcqStatusChoices.IN_DATE) {
      return {
        status,
        color: themeColors.lightGreen,
        details: `In Date until: ${moment(contact.statuses?.factFind?.dateSubmitted)
          .add(3, 'years')
          .format('DD/MM/YYYY')}`,
      };
    }
    if (contact.ecqStatus === OneviewContactEcqStatusChoices.UP_TO_DATE) {
      return {
        status,
        color: themeColors.lightGreen,
        details: `Up to date until: ${moment(contact.statuses?.factFind?.dateSubmitted)
          .add(3, 'years')
          .format('DD/MM/YYYY')}`,
      };
    }

    if (contact.ecqStatus === OneviewContactEcqStatusChoices.OUTDATED) {
      return {
        status,
        color: themeColors.lightAlert,
        details: `Outdated since: ${moment(contact.statuses?.factFind?.dateSubmitted)
          .add(3, 'years')
          .format('DD/MM/YYYY')}`,
      };
    }
    if (contact.ecqStatus === OneviewContactEcqStatusChoices.MISSING) {
      return {
        status,
        color: themeColors.lightAlert,
        details: 'Contact has never completed an ECQ',
      };
    }
    return {
      status,
      color: themeColors.lightAlert,
      details: 'No ECQ data found for this contact',
    };
  };

  const ecqStatus = getECQStatus();

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body1" fontWeight="bold">
          ECQ Status
        </Typography>
        <ColoredChip
          value={ecqStatus.status}
          keepCase
          size="small"
          colorScheme={{[ecqStatus.status]: ecqStatus.color}}
        />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {ecqStatus.details}
      </Typography>
    </Box>
  );
};

const ContactClientPortalStatus: React.FC<{
  contact: ContactRAGStatusesFragment;
}> = ({contact}) => {
  const getClientPortalStatus = () => {
    const rawStatus = contact.statuses?.clientPortal?.status;
    const {clientPortal} = contact.statuses || {};

    if (!rawStatus) {
      return {
        status: 'Unknown',
        color: themeColors.greyLight,
        details: 'Client portal status not available',
      };
    }

    const status = enumToLabel(rawStatus);

    // Build details with date information
    let details = '';
    if (clientPortal?.lastUpdated) {
      details = `Last updated: ${new Date(
        clientPortal.lastUpdated
      ).toLocaleDateString()}`;
    }

    // Map client portal statuses to appropriate colors
    switch (rawStatus) {
      case ClientPortalStatusCategory.confirmed:
        return {
          status,
          color: themeColors.lightGreen,
          details: details || 'Client portal access confirmed',
        };
      case ClientPortalStatusCategory.unconfirmed:
        return {
          status,
          color: themeColors.lightBlue,
          details: details || 'Client portal access pending confirmation',
        };
      case ClientPortalStatusCategory.not_started:
        return {
          status: 'Not Started',
          color: themeColors.lightBlue,
          details: details || 'Client portal setup not yet started',
        };
      case ClientPortalStatusCategory.compromised:
      case ClientPortalStatusCategory.reset_required:
        return {
          status,
          color: themeColors.lightAlert,
          details: details || 'Client portal requires attention',
        };
      case ClientPortalStatusCategory.force_change_password:
        return {
          status: 'Password Change Required',
          color: themeColors.lightBlue,
          details: details || 'Client must change password on next login',
        };
      case ClientPortalStatusCategory.archived:
        return {
          status,
          color: themeColors.greyLight,
          details: details || 'Client portal access has been archived',
        };
      case ClientPortalStatusCategory.does_not_exist:
        return {
          status: 'No Access',
          color: themeColors.lightAlert,
          details: details || 'Client portal access does not exist',
        };
      case ClientPortalStatusCategory.unknown:
      default:
        return {
          status: 'Unknown',
          color: themeColors.greyLight,
          details: details || `Client portal status: ${status}`,
        };
    }
  };

  const portalStatus = getClientPortalStatus();

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body1" fontWeight="bold">
          Client Portal Status
        </Typography>
        <ColoredChip
          value={portalStatus.status}
          keepCase
          size="small"
          colorScheme={{[portalStatus.status]: portalStatus.color}}
        />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {portalStatus.details}
      </Typography>
    </Box>
  );
};

const InvitationEmailStatus: React.FC<{
  invitation: GetReviewInvitationDetails_reviewDetails_invitation | null;
  contact: ContactRAGStatusesFragment;
  emailAuditData?: GetInvitationEmailAudit;
  emailAuditLoading: boolean;
  emailAuditError?: any;
}> = ({invitation, contact, emailAuditData, emailAuditLoading, emailAuditError}) => {
  const reviewId = invitation?.review?.id;

  const getEmailStatusInfo = () => {
    if (!reviewId) {
      return {
        status: 'No Invitation',
        color: themeColors.greyLight,
        details: 'No invitation exists for this review',
      };
    }

    if (emailAuditLoading) {
      return {
        status: 'Loading...',
        color: themeColors.greyLight,
        details: 'Loading email audit information',
      };
    }

    if (emailAuditError) {
      return {
        status: 'Error',
        color: themeColors.lightAlert,
        details: `Failed to load email audit: ${emailAuditError.message}`,
      };
    }

    if (
      !emailAuditData?.getInvitationEmailAudit ||
      emailAuditData.getInvitationEmailAudit.length === 0
    ) {
      return {
        status: 'No Email Sent',
        color: themeColors.greyLight,
        details: 'No invitation emails have been sent for this review',
      };
    }

    const contactEmails = emailAuditData.getInvitationEmailAudit.filter(
      (email) => email.contact.id === contact.id
    );

    if (contactEmails.length === 0) {
      return {
        status: 'No Email Sent',
        color: themeColors.greyLight,
        details: 'No invitation emails sent to this contact',
      };
    }

    const mostRecentEmail = contactEmails.sort(
      (a: any, b: any) => new Date(b.dateSent).getTime() - new Date(a.dateSent).getTime()
    )[0];

    const statusText = mostRecentEmail.status || 'Unknown';
    const formattedDate = new Date(mostRecentEmail.dateSent).toLocaleDateString('en-GB');

    return {
      status: statusText,
      color: statusText.toLowerCase().includes('delivered')
        ? themeColors.lightGreen
        : statusText.toLowerCase().includes('failed')
        ? themeColors.lightAlert
        : themeColors.lightBlue,
      details: `${statusText} on ${formattedDate}`,
    };
  };

  const emailStatus = getEmailStatusInfo();

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body1" fontWeight="bold">
          Email Status
        </Typography>
        <ColoredChip
          value={emailStatus.status}
          keepCase
          size="small"
          colorScheme={{[emailStatus.status]: emailStatus.color}}
        />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {emailStatus.details}
      </Typography>
    </Box>
  );
};

export const InvitationContactDisplay: React.FC<InvitationContactDisplayProps> = ({
  contact,
  invitation,
  accountId,
  emailAuditData,
  emailAuditLoading,
  emailAuditError,
}) => {
  return (
    <Panel
      title={contact.name || contact.simplifiedName || 'Contact'}
      color={themeColors.lightBlue}
      sx={{flex: 1, m: 0.5}}
    >
      <Box
        gap={3}
        flex={1}
        display="flex"
        flexDirection="column"
        // border={`2px solid ${themeColors.greyLight}`}
        // borderRadius={2}
        py={1}
      >
        {/* <Typography variant="h5" component="h5">
          {contact.name || contact.simplifiedName}
        </Typography> */}

        <MeetingPhoneNumbers contact={contact} invitation={invitation} />
        <ContactEmailStatus contact={contact} />

        <ContactATRStatus contact={contact} accountId={accountId} />

        <ContactECQStatus contact={contact} />

        <ContactClientPortalStatus contact={contact} />

        <InvitationEmailStatus
          invitation={invitation}
          contact={contact}
          emailAuditData={emailAuditData}
          emailAuditLoading={emailAuditLoading}
          emailAuditError={emailAuditError}
        />
      </Box>
    </Panel>
  );
};
